import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, Loader2 } from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { showToast } from "@/store/slices/uiSlice";
import {
  fetchUserAccess,
  fetchRevenueCatStatus,
} from "@/store/slices/subscriptionSlice";
import { logger } from "@/utils/logger";
import { useProduct } from "@/hooks/useProduct";

const CheckoutSuccess = () => {
  const config = useProduct();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [isProcessing, setIsProcessing] = useState(true);
  const [paymentVerified, setPaymentVerified] = useState(false);

  const sessionId = searchParams.get("session_id");
  const { user } = useAppSelector((state) => state.auth);

  useEffect(() => {
    const handlePaymentSuccess = async () => {
      if (!sessionId) {
        logger.error("[CheckoutSuccess] No session_id provided");
        dispatch(
          showToast({
            message: config.i18n.checkoutSuccess.noSessionId,
            type: "error",
          })
        );
        navigate("/dashboard");
        return;
      }

      if (!user?.id) {
        logger.error("[CheckoutSuccess] No user authenticated");
        dispatch(
          showToast({
            message: config.i18n.checkoutSuccess.notAuthenticated,
            type: "error",
          })
        );
        navigate("/login");
        return;
      }

      try {
        // 1. Verificar o status do pagamento com o Stripe via Azure Function
        logger.info("[CheckoutSuccess] Verifying payment with Stripe", {
          sessionId,
        });

        const azureFunctionUrl =
          import.meta.env.VITE_AZURE_FUNCTIONS_URL ||
          "https://meeting-gc-functions-v2.azurewebsites.net/api";
        if (!azureFunctionUrl) {
          throw new Error("Azure Functions URL not configured");
        }

        // Call Azure Function to verify payment status
        const verifyResponse = await fetch(
          `${azureFunctionUrl}/verify-stripe-payment`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              sessionId: sessionId,
            }),
          }
        );

        if (!verifyResponse.ok) {
          throw new Error("Failed to verify payment status");
        }

        const verifyData = await verifyResponse.json();
        if (verifyData.status !== "paid") {
          throw new Error("Payment not completed");
        }

        setPaymentVerified(true);

        // 2. Atualizar o status da subscription no RevenueCat
        logger.info(
          "[CheckoutSuccess] Updating RevenueCat subscription status"
        );

        // Force refresh of RevenueCat status - pass undefined to fetch from SDK
        await dispatch(fetchRevenueCatStatus(undefined)).unwrap();

        // 3. Atualizar o estado local do usuário (fetch user access)
        logger.info("[CheckoutSuccess] Refreshing user access data");
        await dispatch(fetchUserAccess()).unwrap();

        // 4. Enviar analytics (optional - can be added later)
        logger.info("[CheckoutSuccess] Payment processing completed", {
          sessionId,
          userId: user.id,
          amount: verifyData.amount_total,
          currency: verifyData.currency,
        });

        dispatch(
          showToast({
            message: "Payment processed successfully! Welcome to premium.",
            type: "success",
          })
        );

        setIsProcessing(false);
      } catch (error) {
        logger.error("[CheckoutSuccess] Error processing payment:", error);
        dispatch(
          showToast({
            message: config.i18n.checkoutSuccess.errorProcessing,
            type: "error",
          })
        );
        navigate("/dashboard");
      }
    };

    handlePaymentSuccess();
  }, [sessionId, user?.id, dispatch, navigate]);

  if (isProcessing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center">
              <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                {config.i18n.checkoutSuccess.processing}
              </h2>
              <p className="text-gray-600">
                {config.i18n.checkoutSuccess.processingMessage}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-emerald-100">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-green-100 flex items-center justify-center">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <CardTitle className="text-2xl text-green-900">
            {config.i18n.checkoutSuccess.approved}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <p className="text-gray-600">
            {config.i18n.checkoutSuccess.activatedMessage}
          </p>

          <div className="bg-green-50 p-4 rounded-lg">
            <h3 className="font-semibold text-green-900 mb-2">
              {config.i18n.checkoutSuccess.welcomeToPremium}
            </h3>
            <ul className="text-sm text-green-800 space-y-1 text-left">
              <li>{config.i18n.checkoutSuccess.benefit1}</li>
              <li>{config.i18n.checkoutSuccess.benefit2}</li>
              <li>{config.i18n.checkoutSuccess.benefit3}</li>
              <li>{config.i18n.checkoutSuccess.benefit4}</li>
            </ul>
          </div>

          <div className="space-y-2">
            <Button onClick={() => navigate("/dashboard")} className="w-full">
              {config.i18n.checkoutSuccess.goToDashboard}
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/profile")}
              className="w-full"
            >
              {config.i18n.checkoutSuccess.manageSubscription}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CheckoutSuccess;
